import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { TENCENT_PLAYER_URL } from "../../constants/index";
import { useStreamToken } from "../../hooks/games";

const TencentPlayer = forwardRef(({ stream }, ref) => {
  const iframeRef = useRef(null);
  const { getStreamToken } = useStreamToken();
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true); // старт мьютед
  const [loaded, setLoaded] = useState(false);

  const initPlayer = useCallback(
    async (streamId, streamName) => {
      try {
        const resp = await getStreamToken(streamId,streamName);
        if (!resp.success) {
          setTimeout(() => initPlayer(streamId, streamName), 10000);
          return;
        }
        const { txSecret, txTime } = resp;
        const src = `${stream.webRtcUrl.split("live/")[0]}live/${streamName}?txSecret=${txSecret}&txTime=${txTime}`;
        const iframe = iframeRef.current;
        if (!iframe) return;

        const iWindow = iframe.contentWindow;
        iframe.onload = () => {
          if (iWindow) {
            iWindow.postMessage({ type: "config", msg: { src } }, "*");
            iWindow.postMessage({ type: "play" }, "*");
            iWindow.postMessage({ type: "mute" }, "*");
          }
        };
      } catch (err) {
        console.error(err);
        setTimeout(() => initPlayer(streamId, streamName), 3000);
      }
    },
    [stream.webRtcUrl, getStreamToken]
  );

  useImperativeHandle(ref, () => ({
    play: () => {
      const iWindow = iframeRef.current?.contentWindow;
      if (iWindow) iWindow.postMessage({ type: "play" }, "*");
      setPaused(false);
    },
    pause: () => {
      const iWindow = iframeRef.current?.contentWindow;
      if (iWindow) iWindow.postMessage({ type: "stop" }, "*");
      setPaused(true);
    },
    stop: () => {
      const iWindow = iframeRef.current?.contentWindow;
      if (iWindow) iWindow.postMessage({ type: "stop" }, "*");
      setPaused(true);
    },
    changeQuality: async (qualityKey) => {
      const q = stream.config.qualities[qualityKey];
      if (!q) return;
      const tokenResp = await getStreamToken(stream.id, q.streamId);
      if (tokenResp.success) {
        const iWindow = iframeRef.current?.contentWindow;
        const url = `${stream.webRtcUrl.split("live/")[0]}live/${q.streamId}?txSecret=${tokenResp.txSecret}&txTime=${tokenResp.txTime}`;
        if (iWindow) iWindow.postMessage({ type: "changeQuality", msg: url }, "*");
      }
    },
    mute: () => {
      const iWindow = iframeRef.current?.contentWindow;
      if (iWindow) iWindow.postMessage({ type: "mute" }, "*");
      setMuted(true);
    },
    unmute: () => {
      const iWindow = iframeRef.current?.contentWindow;
      if (iWindow) iWindow.postMessage({ type: "unmute" }, "*");
      setMuted(false);
    },
    volume: (val) => {
      const iWindow = iframeRef.current?.contentWindow;
      if (iWindow) iWindow.postMessage({ type: "volume", msg: val }, "*");
    },
  }));

  useEffect(() => {
    const streamId = stream.id;
    const streamName = Object.values(stream.config.qualities)[0].streamId;
    initPlayer(streamId, streamName);

    const listener = (event) => {
      if (event.data.type === "onReady") {
        setLoaded(true);
      } else if (event.data.type === "onPlay") {
        setPaused(false);
      } else if (event.data.type === "onPause") {
        setPaused(true);
      } else if (event.data.type === "onError") {
        console.error(event.data.msg);
        setTimeout(() => initPlayer(streamId, streamName), 1000);
      } else if (event.data.type === "onMute") {
        setMuted(true);
      } else if (event.data.type === "onUnmute") {
        setMuted(false);
      }
    };

    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [initPlayer, stream]);

  return (
    <iframe
      ref={iframeRef}
      src={TENCENT_PLAYER_URL}
      style={{ width: "100%", height: "100%", border: "none", zIndex: 0 }}
      allow="autoplay"
      title="TencentPlayer"
    />
  );
});

export default TencentPlayer;